/**
 * KIAN Assistant API Domain
 *
 * Type-safe API client for assistant/chat operations.
 */

import type { RequestFn } from '../http';
import type { KianUsage } from '../types/core';

/** Was ein Gespräch bisher gekostet hat (Backend: services/kian/sessionUsage.ts) */
export interface KianSessionUsage extends KianUsage {
  /** Je Modell, teuerstes zuerst */
  byModel: Array<{ model: string; calls: number; tokensIn: number; tokensOut: number; costEur: number | null }>;
  /** `metrics` = gemessen je Aufruf; `messages` = aus den Nachrichten geschätzt (ältere Gespräche) */
  source: 'metrics' | 'messages';
}

export interface ChatMessageAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  /** Relative API-URL, z. B. /api/assistant/attachments/:id */
  url: string;
}

export interface ChatMessage {
  id: string;
  role: 'USER' | 'ASSISTANT';
  content: string;
  createdAt: string;
  metadata?: Record<string, unknown>;
  attachments?: ChatMessageAttachment[];
}

export interface ChatSession {
  id: string;
  userId: string;
  submissionId: string | null;
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
  pinned?: boolean;
  title: string;
  status: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  lastTouchedAt?: string;
  /** Werkzeuge je Nachricht — ein Admin kann sie für dieses Gespräch erhöhen */
  toolBudget?: SessionToolBudget;
}

export interface SessionToolBudget {
  /** Gilt für die nächste Nachricht */
  perMessage: number;
  /** Globale Einstellung (Einstellungen → Sprachmodell) */
  default: number;
  raised: boolean;
  max: number;
  /** Vorschlag für „Budget erhöhen" — null am Höchstwert */
  nextStep: number | null;
}

export interface ChatSessionListItem {
  id: string;
  title: string;
  status: string;
  submissionId: string | null;
  entityType?: string | null;
  entityId?: string | null;
  entityLabel?: string | null;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantPageContextPayload {
  primary?: { type: string; id: string; label?: string; metadata?: Record<string, unknown> };
  recent?: Array<{ type: string; id: string; label: string; visitedAt?: number }>;
  pinned?: Array<{ type: string; id: string; label?: string }>;
}

export interface AssistantResponse {
  assistantMessage: string;
  sessionId: string;
  messageId: string;
  userMessage?: {
    id: string;
    role: 'USER';
    content: string;
    createdAt: string;
    attachments: ChatMessageAttachment[];
  };
  metadata?: {
    model?: string;
    inputTokens?: number;
    outputTokens?: number;
    costEur?: number;
    tier?: string;
    durationMs?: number;
    /** Von KIAN vorbereitete Entwürfe — erscheinen als Karten unter der Antwort */
    actionIds?: string[];
  };
}

export function createAssistantApi(request: RequestFn) {
  return {
    /**
     * Create a new chat session
     */
    async createSession(data: {
      submissionId?: string;
      title?: string;
      entityType?: string | null;
      entityId?: string | null;
      entityLabel?: string | null;
      forceNew?: boolean;
    }) {
      return request<{ sessionId: string; isNew: boolean }>('/assistant/session', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Send a message to the assistant (with optional file attachments)
     */
    async sendMessage(
      sessionId: string,
      data: {
        content: string;
        pageContext?: AssistantPageContextPayload;
        hints?: Record<string, unknown>;
      },
      files?: File[]
    ) {
      if (files && files.length > 0) {
        // Use FormData for file upload
        const formData = new FormData();
        formData.append('content', data.content);
        if (data.pageContext) {
          formData.append('pageContext', JSON.stringify(data.pageContext));
        }
        if (data.hints && Object.keys(data.hints).length > 0) {
          formData.append('hints', JSON.stringify(data.hints));
        }
        files.forEach((file) => {
          formData.append('files', file);
        });

        return request<AssistantResponse>(`/assistant/chat?sessionId=${sessionId}`, {
          method: 'POST',
          kianUsageInline: true,
          body: formData,
        });
      } else {
        // Use JSON for text-only messages
        return request<AssistantResponse>(`/assistant/chat?sessionId=${sessionId}`, {
          method: 'POST',
          kianUsageInline: true,
          body: JSON.stringify(data),
        });
      }
    },

    async getKianConfig() {
      return request<{ flyoutEnabled: boolean }>('/assistant/kian-config', { method: 'GET' });
    },

    async getRateLimitStatus() {
      return request<{ usedPerMin: number; maxPerMin: number; resetInMs: number }>(
        '/assistant/rate-limit',
        { method: 'GET' }
      );
    },

    async pinSession(sessionId: string, pinned: boolean) {
      return request<{ success: boolean }>(`/assistant/session/${sessionId}/pin`, {
        method: 'PATCH',
        body: JSON.stringify({ pinned }),
      });
    },

    /** Nur Admins: Werkzeug-Budget je Nachricht für dieses Gespräch (`null` = globale Einstellung) */
    async setToolBudget(sessionId: string, perMessage: number | null) {
      return request<{ toolBudget: SessionToolBudget }>(`/assistant/session/${sessionId}/tool-budget`, {
        method: 'PATCH',
        body: JSON.stringify({ perMessage }),
      });
    },

    async touchSession(sessionId: string) {
      return request<{ success: boolean }>(`/assistant/session/${sessionId}/touch`, {
        method: 'POST',
      });
    },

    async postFeedback(messageId: string, body: { sessionId: string; rating: 1 | -1; comment?: string }) {
      return request<{ success: boolean }>(`/assistant/messages/${messageId}/feedback`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },

    /**
     * Load full session with all messages
     */
    async getSession(sessionId: string) {
      return request<ChatSession>(`/assistant/chat/${sessionId}`);
    },

    /** Tokens und Kosten des ganzen Gesprächs (alle KIAN-Aufrufe) — `null`, solange nichts lief */
    async getSessionUsage(sessionId: string) {
      return request<{ usage: KianSessionUsage | null }>(`/assistant/session/${sessionId}/usage`);
    },

    /**
     * List user's chat sessions
     */
    async getSessions(options?: {
      limit?: number;
      offset?: number;
      status?: string;
      entityType?: string;
      entityId?: string;
    }) {
      const params = new URLSearchParams();
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      if (options?.status) params.set('status', options.status);
      if (options?.entityType) params.set('entityType', options.entityType);
      if (options?.entityId) params.set('entityId', options.entityId);

      return request<{
        sessions: ChatSessionListItem[];
        total: number;
      }>(`/assistant/sessions${params.toString() ? '?' + params.toString() : ''}`);
    },

    /**
     * Archive a session
     */
    async archiveSession(sessionId: string) {
      return request<{ success: boolean }>(`/assistant/session/${sessionId}/archive`, {
        method: 'POST',
      });
    },

    /**
     * Restore an archived session to ACTIVE status
     */
    async unarchiveSession(sessionId: string) {
      return request<{ success: boolean }>(`/assistant/session/${sessionId}/unarchive`, {
        method: 'POST',
      });
    },

    /**
     * Delete a session completely
     */
    async deleteSession(sessionId: string) {
      return request<{ success: boolean }>(`/assistant/session/${sessionId}`, {
        method: 'DELETE',
      });
    },

    /**
     * Extract text from an image for pre-filling the chat input.
     * Server-side vision analysis still runs on send.
     */
    async ocrPreview(file: File) {
      const form = new FormData();
      form.append('file', file);
      return request<{ text: string }>('/assistant/ocr-preview', {
        method: 'POST',
        body: form,
      });
    },
  };
}
